// src/components/formularios/AsignarFormularioDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { asignarFormulario, getAsignaciones, type AsignarFormularioDto } from '../../api/formularios';
import { useUsuarios, type Usuario } from '../../hooks/useUsuarios';

interface Props {
  open: boolean;
  formularioId: number;
  onClose: () => void;
  onSaved: () => void;
}

const ROLES = [
  'admin',
  'att_familias',
  'att_alumnado',
  'att_profesorado',
  'admisiones',
  'facturacion',
  'hr',
  'consultor',
  'profesor',
] as const;

export default function AsignarFormularioDialog({ open, formularioId, onClose, onSaved }: Props) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Usuario[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { rows: usuarios = [], loading: loadingUsuarios } = useUsuarios({ soloActivos: true });

  useEffect(() => {
    if (open && formularioId) {
      loadAssignments();
    }
  }, [open, formularioId]);

  const loadAssignments = async () => {
    setLoadingAssignments(true);
    try {
      const data = await getAsignaciones(formularioId);
      setExistingAssignments(data);
    } catch (error) {
      // Error loading assignments
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedRoles.length === 0 && selectedUsers.length === 0) {
      enqueueSnackbar('Selecciona al menos un rol o usuario', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const payload: AsignarFormularioDto = {
        roles: selectedRoles.length > 0 ? selectedRoles : undefined,
        usuarioIds: selectedUsers.length > 0 ? selectedUsers.map((u) => u.id) : undefined,
      };
      await asignarFormulario(formularioId, payload);
      enqueueSnackbar('Formulario asignado correctamente', { variant: 'success' });
      setSelectedRoles([]);
      setSelectedUsers([]);
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error al asignar formulario', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuarios por roles seleccionados
  const usuariosFiltrados = selectedRoles.length > 0
    ? usuarios.filter((user) => selectedRoles.includes(user.rol))
    : usuarios;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#000',
          color: '#fff',
          fontWeight: 'bold',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          mb: 3,
        }}
      >
        Asignar Formulario
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#fff', pt: 3, pb: 2 }}>
        <Stack spacing={3}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Asignar por Rol
            </Typography>
            <Autocomplete
              multiple
              options={ROLES}
              value={selectedRoles}
              onChange={(_, newValue) => setSelectedRoles(newValue)}
              renderInput={(params) => <TextField {...params} placeholder="Selecciona roles" />}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} {...getTagProps({ index })} key={option} />
                ))
              }
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Asignar a Usuarios Específicos
              {selectedRoles.length > 0 && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  (Filtrando por: {selectedRoles.join(', ')})
                </Typography>
              )}
            </Typography>
            <Autocomplete
              multiple
              options={usuariosFiltrados}
              value={selectedUsers}
              onChange={(_, newValue) => setSelectedUsers(newValue)}
              getOptionLabel={(option) => `${option.nombreCompleto || option.email} (${option.rol})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingUsuarios}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Busca usuarios..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsuarios ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {loadingAssignments && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loadingAssignments && existingAssignments.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Asignaciones Actuales ({existingAssignments.length})
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {existingAssignments.map((asig, idx) => (
                  <Chip
                    key={idx}
                    label={`${asig.usuarioNombre || `Usuario #${asig.usuarioId}`} ${asig.rol ? `(${asig.rol})` : ''}`}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                    color={asig.activa ? 'primary' : 'default'}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', gap: 1, p: 2, backgroundColor: '#fff', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: '#21a598' }}>
          CANCELAR
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ backgroundColor: '#21a598', '&:hover': { backgroundColor: '#1b8a85' } }}>
          {loading ? 'ASIGNANDO...' : 'ASIGNAR'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

